import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSetAtom } from 'jotai';
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { localeAtom, type Locale } from '../../state/localeState';

const LOCALES: { value: Locale; messageId: string }[] = [
  { value: 'fr', messageId: 'language.fr' },
  { value: 'en', messageId: 'language.en' },
];

export function LanguageDropdown() {
  const intl = useIntl();
  const setLocale = useSetAtom(localeAtom);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentLocale = intl.locale as Locale;
  const currentLabel = intl.formatMessage({
    id: currentLocale === 'fr' ? 'language.fr' : 'language.en',
  });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (locale: Locale) => {
    setLocale(locale);
    handleClose();
  };

  return (
    <>
      <Button
        aria-label={intl.formatMessage({ id: 'language.label' })}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
        startIcon={<LanguageIcon />}
        sx={{
          color: 'text.primary',
          textTransform: 'none',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography component="span" variant="body2" fontWeight={500}>
          {currentLabel}
        </Typography>
      </Button>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        {LOCALES.map(({ value, messageId }) => (
          <MenuItem key={value} onClick={() => handleSelect(value)}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              {currentLocale === value ? (
                <CheckIcon fontSize="small" color="primary" />
              ) : null}
            </ListItemIcon>
            <ListItemText primary={intl.formatMessage({ id: messageId })} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
